import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';

/**
 * OAuth Callback Handler for Popup Flow
 *
 * This page is loaded in the popup after OAuth completes.
 * It extracts the token from URL and stores it in localStorage,
 * then closes the popup to return control to parent window.
 *
 * This approach bypasses CORS/Same-Origin Policy restrictions that
 * prevent reading popup.location.href on mobile browsers (iOS Safari).
 */
const OAuthCallbackHandler: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    try {
      console.log('OAuth callback handler loaded');

      // Extract token from this page's URL (no CORS issue)
      const searchParams = new URLSearchParams(location.search);
      const token = searchParams.get('instagram_token');
      const error = searchParams.get('instagram_error');

      if (error) {
        console.error('OAuth callback error:', error);
        // Store error in localStorage for parent to read
        localStorage.setItem('instagram_oauth_error', error);
        // Close popup after short delay
        setTimeout(() => {
          window.close();
        }, 1000);
        return;
      }

      if (token) {
        console.log('OAuth callback received token, storing in localStorage');
        // Store token with timestamp for parent window to detect
        const callbackData = {
          token: token,
          timestamp: Date.now()
        };
        localStorage.setItem('instagram_oauth_callback', JSON.stringify(callbackData));

        // Close popup after short delay to ensure localStorage is written
        setTimeout(() => {
          window.close();
        }, 500);
      } else {
        console.warn('No token or error in callback URL');
        localStorage.setItem('instagram_oauth_error', 'No token received');
        setTimeout(() => {
          window.close();
        }, 1000);
      }
    } catch (error) {
      console.error('Error in OAuth callback handler:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      localStorage.setItem('instagram_oauth_error', errorMessage);
      setTimeout(() => {
        window.close();
      }, 1000);
    }
  }, [location]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      bgcolor="background.default"
    >
      <CircularProgress size={60} />
      <Typography variant="h6" sx={{ mt: 3 }}>
        Processing Instagram connection...
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        This window will close automatically
      </Typography>
    </Box>
  );
};

export default OAuthCallbackHandler;
